import { Suspense, useState } from 'react';

import { getCookie } from '@api/HelperAPI';
import { useDrawdownRepaymentSchedule } from '@api/lendingAPI/lapiHooks';
import { Constants } from '@constants';
import { numbersOnly } from '@forms/helpers/normalisers';
import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { CurrencyInput, Table } from '@iwoca/orion';
import { Skeleton } from '@ui';
import _ from 'lodash';
import { FormattedMessage } from 'react-intl';

import { PaymentPlanSchedule } from './PaymentPlanSchedule';
import { ErrorBoundary } from '../../../../../errorBoundary/ErrorBoundary';
import FallbackErrorModal from '../../../../modals/fallbackErrorModal/FallbackErrorModal';
import { RCF_CASHLESS_OPENING_COOKIE } from '../RCFApprovedView';

export const PaymentPlanModalContent = ({
    offer,
    initialAmount,
    isInRepaymentFreePeriod
}: {
    offer: Pick<TFormalOffer, 'offer_id' | 'max_amount'>;
    initialAmount: number;
    isInRepaymentFreePeriod?: boolean;
}) => {
    const [drawdownAmount, setDrawdownAmount] = useState(initialAmount);
    const maxAmount = offer.max_amount!;

    const isCashlessActivation =
        getCookie(RCF_CASHLESS_OPENING_COOKIE) === 'true';
    const minAmount = isCashlessActivation ? 0 : Constants.MIN_DRAW_DOWN_AMOUNT;

    const debouncedSetAmount = _.debounce(
        (value: number) => setDrawdownAmount(value),
        100
    );

    const locale = 'de-DE';

    return (
        <ErrorBoundary
            fallback={(error) => <FallbackErrorModal error={error} />}
        >
            <h3 className="mb-s mt-s text-2xl font-bold">
                <FormattedMessage
                    id="_.rcf_approved_view.payment_plan_modal_title"
                    defaultMessage="Indicative payment plan"
                />
            </h3>
            <div className="mb-m">
                <div className="flex items-center gap-s">
                    <span className="text-m font-bold">
                        <FormattedMessage
                            id="_.rcf_approved_view.payment_plan_if_i_withdraw"
                            defaultMessage="If I withdraw"
                        />
                    </span>
                    <CurrencyInput
                        className="w-[140px]"
                        currency={{ locale, currencySymbol: true }}
                        min={minAmount}
                        max={maxAmount}
                        value={String(drawdownAmount)}
                        onChange={(e) => {
                            const numericValue =
                                parseInt(numbersOnly(e.target.value), 10) || 0;
                            debouncedSetAmount(
                                Math.min(numericValue, maxAmount)
                            );
                        }}
                    />
                    <span className="text-m font-bold">
                        <FormattedMessage
                            id="_.rcf_approved_view.payment_plan_today"
                            defaultMessage="today"
                        />
                    </span>
                </div>
            </div>
            <Suspense fallback={<ScheduleSkeleton />}>
                <ScheduleContent
                    offer={offer}
                    drawdownAmount={drawdownAmount}
                    isInRepaymentFreePeriod={isInRepaymentFreePeriod}
                />
            </Suspense>
        </ErrorBoundary>
    );
};

const ScheduleContent = ({
    offer,
    drawdownAmount,
    isInRepaymentFreePeriod
}: {
    offer: Pick<TFormalOffer, 'offer_id' | 'max_amount'>;
    drawdownAmount: number;
    isInRepaymentFreePeriod?: boolean;
}) => {
    const maxAmount = offer.max_amount!;
    const isValid =
        drawdownAmount === 0 ||
        (drawdownAmount >= Constants.MIN_DRAW_DOWN_AMOUNT &&
            drawdownAmount <= maxAmount);

    const { data } = useDrawdownRepaymentSchedule({
        offerId: offer.offer_id!,
        drawdownAmount,
        suspense: true,
        enabled: isValid
    });

    if (!data) return <ScheduleSkeleton />;

    return (
        <PaymentPlanSchedule
            repaymentSchedule={data}
            isInRepaymentFreePeriod={isInRepaymentFreePeriod}
        />
    );
};

const SKELETON_ROWS = 4;

const ScheduleSkeleton = () => (
    <Table.Root data-testid="payment-plan-skeleton">
        <Table.Header>
            <Table.Row>
                <Table.HeaderCell>
                    <Skeleton className="h-[16px] w-[60px]" />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <Skeleton className="h-[16px] w-[70px]" />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <Skeleton className="h-[16px] w-[60px]" />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <Skeleton className="h-[16px] w-[100px]" />
                </Table.HeaderCell>
            </Table.Row>
        </Table.Header>
        <Table.Body>
            {Array.from({ length: SKELETON_ROWS }, (_, i) => (
                <Table.Row key={i}>
                    <Table.Cell>
                        <Skeleton className="h-[16px] w-[80px]" />
                    </Table.Cell>
                    <Table.Cell>
                        <Skeleton className="h-[16px] w-[50px]" />
                    </Table.Cell>
                    <Table.Cell>
                        <Skeleton className="h-[16px] w-[60px]" />
                    </Table.Cell>
                    <Table.Cell>
                        <Skeleton className="h-[16px] w-[60px]" />
                    </Table.Cell>
                </Table.Row>
            ))}
        </Table.Body>
    </Table.Root>
);
